/**
 * Transport abstraction for the generated Compass API services.
 *
 * The generated services in `./services` never talk to axios, fetch, or any other
 * HTTP library directly — they call the `Transport` passed to their factory. Each
 * consuming app supplies its own adapter, which keeps this package free of runtime
 * dependencies and lets each app retain its own auth, retry and error-reporting
 * behaviour:
 *
 * - `ui/dashboard`     — axios adapter over the existing `apiClient` (token refresh,
 *                        toasts, axios-retry, logout redirects all keep working)
 * - `ui/admin-dashboard` — axios adapter over its own `apiClient`
 * - `ui/ai-chat`       — `fetch` adapter, built per request from the caller's token
 *
 * ## Why there is no module-level transport
 *
 * This module deliberately exports no configured singleton and holds no mutable
 * state. `ui/ai-chat` is a server that forwards *each user's* bearer token per
 * request; a shared module-level client could serve one user's token on another
 * user's concurrent request, which for a HIPAA product means leaking PHI across
 * sessions. Requiring the transport to be passed in makes that impossible by
 * construction rather than by convention.
 */

/** Per-request options. Transport-agnostic subset of what axios/fetch both support. */
export interface RequestOptions {
  /**
   * Query string parameters.
   *
   * Typed as `object` rather than `Record<string, unknown>` because the generated
   * services pass proto request interfaces straight through, and those have no index
   * signature. Adapters should narrow with `params as Record<string, unknown>`.
   */
  params?: object;
  /**
   * Request body for verbs whose signature takes no body argument — in practice the few
   * DELETEs that send one (e.g. `RoleService.RemovePermissionFromRole`).
   */
  data?: unknown;
  /** Additional request headers. */
  headers?: Record<string, string>;
  /** Abort signal for cancellation. */
  signal?: AbortSignal;
  /** Request timeout in milliseconds. */
  timeout?: number;
  /** Expected response shape. Defaults to 'json'. */
  responseType?: 'json' | 'blob' | 'arraybuffer' | 'text';
}

/**
 * The HTTP surface the generated services depend on.
 *
 * Implementations return the **unwrapped response body** — an axios adapter must
 * map `response.data`, not hand back the whole `AxiosResponse`. This is what lets a
 * `fetch` adapter be a natural implementation rather than a contorted one.
 *
 * Paths passed to these methods are relative to the transport's own base URL, and
 * already have their path parameters substituted by `getApiPath`.
 */
export interface Transport {
  get<T>(url: string, options?: RequestOptions): Promise<T>;
  post<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(url: string, options?: RequestOptions): Promise<T>;

  /**
   * multipart/form-data POST. Optional — only a handful of endpoints (asset,
   * campaign and case-record uploads) need it, so transports that never upload
   * can leave it out.
   *
   * Implementations MUST NOT set `Content-Type` themselves; the runtime has to
   * generate the multipart boundary.
   */
  postForm?<T>(url: string, form: FormData, options?: RequestOptions): Promise<T>;

  /**
   * Server-sent-event streaming. Optional — only the case-record watch endpoints
   * use it, so non-streaming transports can leave it out.
   */
  stream?<T>(url: string, body: unknown, options?: StreamOptions): AsyncIterable<T>;
}

/** Options for streaming (SSE) calls. */
export interface StreamOptions extends RequestOptions {
  /** Called when the connection opens. */
  onOpen?: () => void;
  /** Called on connection error. */
  onError?: (error: Error) => void;
  /** Called when the connection closes. */
  onClose?: () => void;
}

/**
 * Thrown when generated code calls an optional transport method the supplied
 * transport does not implement. Names both the method and the calling service so
 * the failure is self-explanatory instead of a bare `undefined is not a function`.
 */
export class TransportCapabilityError extends Error {
  constructor(method: 'postForm' | 'stream', serviceMethod: string) {
    super(
      `Transport does not implement ${method}(), which ${serviceMethod} requires. ` +
        `Add ${method} to the transport passed to createServices().`,
    );
    this.name = 'TransportCapabilityError';
  }
}

/** Narrowing helper used by generated multipart methods. */
export const requirePostForm = (
  transport: Transport,
  serviceMethod: string,
): NonNullable<Transport['postForm']> => {
  if (!transport.postForm) {
    throw new TransportCapabilityError('postForm', serviceMethod);
  }
  return transport.postForm.bind(transport);
};

/** Narrowing helper used by generated streaming methods. */
export const requireStream = (
  transport: Transport,
  serviceMethod: string,
): NonNullable<Transport['stream']> => {
  if (!transport.stream) {
    throw new TransportCapabilityError('stream', serviceMethod);
  }
  return transport.stream.bind(transport);
};
